/**
 * K108-R Batch 2 — extension-evidence read model (pure aggregation).
 *
 * Derives a per-session engagement summary from raw
 * `training_session_extension_evidence` rows (written by the Companion via
 * `record-training-extension-evidence`) + the owning `training_consumption_-
 * sessions` row. NO DB change: reads the existing table/columns + `metadata`
 * JSONB (which already carries K108-P/Q fields).
 *
 * HONEST BY DESIGN: confidence is engagement only, capped at 'medium' — this
 * never asserts verified completion. When there is no evidence (Safari 403 / no
 * Companion), it degrades to `monitoredPercent: null` + `evidenceUnavailable`.
 */

export type MonitoringConfidence = 'low' | 'medium';

/** Minimal session shape needed for aggregation. */
export interface MonitoringSessionInput {
  id: string;
  expected_duration_minutes: number | null;
}

/** Minimal evidence-row shape needed for aggregation. */
export interface MonitoringEvidenceRow {
  session_id: string;
  evidence_type: string;
  active_seconds: number | null;
  visible_seconds: number | null;
  played_seconds: number | null;
  submitted_at: string | null;
  metadata: Record<string, unknown> | null;
}

export interface TrainingSessionMonitoringSummary {
  sessionId: string;
  /**
   * K108-V: OBSERVED ENGAGEMENT — active watch seconds / actual video duration,
   * capped 100. This is NOT the max reached position (which a seek/currentTime jump
   * can inflate). Null when no trustworthy duration exists (then use observedMinutes).
   */
  monitoredPercent: number | null;
  /**
   * MAX REACHED POSITION in the video (%). Position only — NEVER the watched/
   * engagement metric (a forward seek pushes this up without watching).
   */
  maxVideoProgressPercent: number | null;
  /** Actual provider video duration (seconds), from extension evidence videoDurationSec. */
  videoDurationSec: number | null;
  activeSeconds: number | null;
  visibleSeconds: number | null;
  playedSeconds: number | null;
  /** Observed engagement in whole minutes (activeSeconds/60) — for the no-duration fallback display. */
  observedMinutes: number | null;
  /** Engagement confidence — capped at medium; NEVER verified completion. */
  confidence: MonitoringConfidence | null;
  lastActivityAt: string | null;
  approvedResourceKey: string | null;
  currentResourceKey: string | null;
  resourceMatched: boolean | null;
  resourceMismatch: boolean;
  paused: boolean;
  stopped: boolean;
  /** True when there are no evidence rows at all (Safari 403 / no Companion). */
  evidenceUnavailable: boolean;
}

export function emptyMonitoringSummary(sessionId: string): TrainingSessionMonitoringSummary {
  return {
    sessionId,
    monitoredPercent: null,
    maxVideoProgressPercent: null,
    videoDurationSec: null,
    activeSeconds: null,
    visibleSeconds: null,
    playedSeconds: null,
    observedMinutes: null,
    confidence: null,
    lastActivityAt: null,
    approvedResourceKey: null,
    currentResourceKey: null,
    resourceMatched: null,
    resourceMismatch: false,
    paused: false,
    stopped: false,
    evidenceUnavailable: true,
  };
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

/** Cap any raw confidence to low|medium — 'high'/'verified' clamp to 'medium'. */
function normalizeConfidence(raw: unknown): MonitoringConfidence | null {
  if (raw === 'low') return 'low';
  if (raw === 'medium' || raw === 'high' || raw === 'verified' || raw === 'complete' || raw === 'completed') {
    return 'medium';
  }
  return null;
}

/** Terminal stop reasons (a PAUSE like focus/hidden/resource_mismatch is NOT terminal). */
const TERMINAL_STOP_REASONS = new Set(['tab_closed', 'expired', 'navigated_away']);

export function aggregateSessionMonitoring(
  session: MonitoringSessionInput,
  rows: MonitoringEvidenceRow[],
): TrainingSessionMonitoringSummary {
  if (!rows || rows.length === 0) return emptyMonitoringSummary(session.id);

  // Chronological order; the "current state" is the latest row's metadata.
  const sorted = [...rows].sort((a, b) => (a.submitted_at ?? '').localeCompare(b.submitted_at ?? ''));
  const latest = sorted[sorted.length - 1];
  const latestMeta = (latest.metadata ?? {}) as Record<string, unknown>;

  // Cumulative counters — the extension accrues, so the MAX across rows is the total.
  let maxVideoPct: number | null = null;
  let videoDurationSec: number | null = null;
  let visibleSeconds: number | null = null;
  let playedSeconds: number | null = null;
  let confidence: MonitoringConfidence | null = null;
  let lastActivityAt: string | null = null;
  let stopped = false;

  // K108-Y CUMULATIVE active seconds across monitoring RUNS. A resume opens a fresh
  // provider tab whose monitor restarts active_seconds at 0, so active_seconds can
  // DROP mid-history. Sum each run's max (a drop marks a new run) rather than taking
  // the global max — otherwise resumed watch time would be undercounted. Single-run
  // sessions (no drop) yield exactly the run max, so this is backward-compatible.
  let activeSeconds: number | null = null;
  let activeCumulative: number | null = null;
  let activeRunMax = 0;
  let prevActive: number | null = null;

  for (const r of sorted) {
    const meta = (r.metadata ?? {}) as Record<string, unknown>;

    // Actual provider video duration — the MAX across rows ignores the early
    // page-load glitch (e.g. a momentary 6s reading before the real ~2027s loads).
    const dur = num(meta.videoDurationSec);
    if (dur != null && dur > 0 && (videoDurationSec == null || dur > videoDurationSec)) {
      videoDurationSec = dur;
    }

    // MAX REACHED POSITION only (NOT engagement): prefer metadata; else derive.
    let rowVideoPct = num(meta.maxVideoProgressPercent);
    if (rowVideoPct == null) {
      const played = num(r.played_seconds);
      if (played != null && dur != null && dur > 0) rowVideoPct = Math.floor((played / dur) * 100);
    }
    if (rowVideoPct != null) rowVideoPct = Math.min(100, Math.max(0, rowVideoPct));
    if (rowVideoPct != null && (maxVideoPct == null || rowVideoPct > maxVideoPct)) maxVideoPct = rowVideoPct;

    const a = num(r.active_seconds);
    if (a != null) {
      // A drop below the previous run's counter = a new monitoring run (resume).
      if (prevActive != null && a < prevActive) {
        activeCumulative = (activeCumulative ?? 0) + activeRunMax;
        activeRunMax = 0;
      }
      if (a > activeRunMax) activeRunMax = a;
      prevActive = a;
    }
    const v = num(r.visible_seconds);
    if (v != null && (visibleSeconds == null || v > visibleSeconds)) visibleSeconds = v;
    const p = num(r.played_seconds);
    if (p != null && (playedSeconds == null || p > playedSeconds)) playedSeconds = p;

    // Confidence: any medium → medium, else low if any low.
    const c = normalizeConfidence(meta.confidence);
    if (c === 'medium') confidence = 'medium';
    else if (c === 'low' && confidence == null) confidence = 'low';

    if (r.submitted_at && (lastActivityAt == null || r.submitted_at > lastActivityAt)) {
      lastActivityAt = r.submitted_at;
    }

    // Terminal stop: an explicit terminal stopReason, or an extension_timeout.
    const stopReason = str(meta.stopReason);
    if ((stopReason && TERMINAL_STOP_REASONS.has(stopReason)) || r.evidence_type === 'extension_timeout') {
      stopped = true;
    }
  }

  // Close out the final run — total watched = sum of every run's max.
  if (prevActive != null) activeSeconds = (activeCumulative ?? 0) + activeRunMax;

  // K108-V OBSERVED ENGAGEMENT = active watch seconds / actual video duration.
  // Denominator: the ACTUAL provider video duration (from evidence) when available;
  // fall back to the session's expected duration ONLY if no evidence duration. The
  // max reached position (maxVideoPct) is deliberately NOT used here — a currentTime
  // jump/seek must not inflate engagement. Active seconds already exclude seeks.
  const expectedSec =
    typeof session.expected_duration_minutes === 'number' && session.expected_duration_minutes > 0
      ? session.expected_duration_minutes * 60
      : null;
  const durationSec = videoDurationSec ?? expectedSec;
  const monitoredPercent =
    durationSec != null && durationSec > 0 && activeSeconds != null
      ? Math.min(100, Math.round((activeSeconds / durationSec) * 100))
      : null;
  // No trustworthy duration → surface minutes observed instead of a fake percent.
  const observedMinutes = activeSeconds != null ? Math.round(activeSeconds / 60) : null;

  // Current-state flags from the latest row.
  const resourceMatched =
    typeof latestMeta.resourceMatched === 'boolean' ? (latestMeta.resourceMatched as boolean) : null;
  const latestReason = str(latestMeta.reason);
  const resourceMismatch = resourceMatched === false || latestReason === 'resource_mismatch';
  const pausedReason = str(latestMeta.pausedReason);
  const paused = !stopped && (!!pausedReason || resourceMismatch);

  return {
    sessionId: session.id,
    monitoredPercent,
    maxVideoProgressPercent: maxVideoPct,
    videoDurationSec,
    activeSeconds,
    visibleSeconds,
    playedSeconds,
    observedMinutes,
    confidence,
    lastActivityAt,
    approvedResourceKey: str(latestMeta.approvedResourceKey),
    currentResourceKey: str(latestMeta.currentResourceKey),
    resourceMatched,
    resourceMismatch,
    paused,
    stopped,
    evidenceUnavailable: false,
  };
}
